import React, {useState} from 'react'
import {Item} from 'react-contexify'
import type {StorageRoot} from '@/app/modules/widgets/core/storage'
import type {CustomFileData} from '@/shared/utils/crud-helper/models'
import {useShares, useSharingAccess} from './hooks'
import {SHARED_FOLDER_ICON} from './constants'
import {ShareEligibility, shareEligibility} from './shareEligibility'
import {ShareFolderDialog} from './ShareFolderDialog'
import type {ShareView} from './types'

/**
 * The folder a "Share folder…" click is about: the one selected folder, or, with nothing selected,
 * the folder on screen (`''` at the root). `null` when the selection is files or several items.
 */
export function shareTargetFolder(
  selected: CustomFileData[],
  currentFolderSegments: string[]
): string | null {
  if (selected.length === 1) {
    return selected[0].isDir ? selected[0].id : null
  }
  if (selected.length === 0) {
    const relative = currentFolderSegments.filter(Boolean).join('/')
    return relative ? `${relative}/` : ''
  }
  return null
}

interface ShareFolderMenuOptions {
  root: StorageRoot
  selectedFiles: () => CustomFileData[]
  /** The breadcrumb, `['', 'a', 'b']` for `a/b/`. */
  currentFolder: () => string[]
}

/**
 * "Share folder…" for the Files context menu, plus the dialog it opens. Only for My Files, and only
 * when sharing is on for this user (E6): otherwise `items` renders nothing at all.
 *
 * The dialog lives outside the menu, which unmounts its items as soon as it closes.
 */
export function useShareFolderMenu({root, selectedFiles, currentFolder}: ShareFolderMenuOptions) {
  const {sharing} = useSharingAccess()
  const shares = useShares()
  const [open, setOpen] = useState<{folderPath: string; share?: ShareView} | null>(null)
  const available = sharing && root.kind === 'own'

  const target = () => shareTargetFolder(selectedFiles(), currentFolder())
  const eligibility = (): ShareEligibility | null => {
    const folder = target()
    return folder === null ? null : shareEligibility(folder, shares.sharedByMe)
  }

  // An array, not a fragment: the menu clones each child to pass it the trigger props, and only
  // flattens arrays.
  const items = available
    ? [
        <MenuSeparator key='share-folder-separator' hidden={() => target() === null} />,
        <Item
          key='share-folder'
          id='context-share-folder'
          hidden={() => target() === null}
          disabled={() => !shares.isLoaded || eligibility()?.kind === 'blocked'}
          onClick={() => {
            // react-contexify runs onClick on Enter even for a disabled item, so check again.
            const folder = target()
            const verdict = eligibility()
            if (!shares.isLoaded || folder === null || !verdict || verdict.kind === 'blocked')
              return
            setOpen({
              folderPath: folder,
              share: verdict.kind === 'shared' ? verdict.share : undefined,
            })
          }}
        >
          <ShareFolderMenuLabel
            eligibility={eligibility}
            pending={
              shares.isLoaded ? null : shares.isLoading || shares.isFetching ? 'loading' : 'error'
            }
          />
        </Item>,
      ]
    : null

  const dialog = open ? (
    <ReactEventBoundary>
      <ShareFolderDialog
        folderPath={open.folderPath}
        share={open.share}
        onHide={() => setOpen(null)}
      />
    </ReactEventBoundary>
  ) : null

  return {items, dialog}
}

/**
 * The dialog is portalled to `<body>`, but React still bubbles its events up the component tree,
 * into the file table that renders the menu: a click would reset the selection and a right-click
 * would open the file menu over the dialog. Stop them here. Key events are left alone: the modal
 * closes on Escape through a document listener, and the table's shortcuts ignore the dialog anyway.
 */
const stop = (e: React.SyntheticEvent) => e.stopPropagation()
const ReactEventBoundary: React.FC<{children: React.ReactNode}> = ({children}) => (
  <div
    style={{display: 'contents'}}
    onClick={stop}
    onDoubleClick={stop}
    onContextMenu={stop}
    onDragStart={stop}
    onDragOver={stop}
    onDrop={stop}
  >
    {children}
  </div>
)

/** react-contexify's Separator can't be hidden; this one disappears with the item it introduces. */
const MenuSeparator: React.FC<{hidden: () => boolean}> = ({hidden}) =>
  hidden() ? null : <div className='react-contexify__separator' />

/** Rendered when the menu opens, so it reads the selection of that moment. */
const ShareFolderMenuLabel: React.FC<{
  eligibility: () => ShareEligibility | null
  /** Set while the share list is not there to check against. */
  pending: 'loading' | 'error' | null
}> = ({eligibility, pending}) => {
  const verdict = eligibility()
  const label = verdict?.kind === 'shared' ? 'Manage sharing…' : 'Share folder…'
  const hint =
    pending === 'loading'
      ? 'Checking your shared folders…'
      : pending === 'error'
      ? "Couldn't load your shared folders. Try again later."
      : verdict?.kind === 'blocked'
      ? verdict.message
      : null
  return (
    <span className='d-flex align-items-start gap-2' data-testid='context-share-folder-label'>
      <i className={`${SHARED_FOLDER_ICON} mt-1`} aria-hidden='true' />
      <span className='d-flex flex-column'>
        <span>{label}</span>
        {hint && (
          <span className='ob-sharing-menu-hint' data-testid='context-share-folder-hint'>
            {hint}
          </span>
        )}
      </span>
    </span>
  )
}
